import React, { useState, useEffect, useRef, useCallback } from 'react'
import { GoogleMap, LoadScript } from '@react-google-maps/api'

const containerStyle = {
  width: '100%',
  height: '100%',
  borderRadius: '12px',
  position: 'relative',
}

const defaultCenter = {
  lat: 37.4275,
  lng: -122.1697,
}

// Custom map styles to match your app's theme
const mapStyles = [
  {
    featureType: 'all',
    elementType: 'geometry',
    stylers: [{ color: '#ffffff' }],
  },
  {
    featureType: 'water',
    elementType: 'geometry',
    stylers: [{ color: '#E3F2FD' }],
  },
  {
    featureType: 'road',
    elementType: 'geometry',
    stylers: [{ color: '#F5F5F5' }],
  },
]

const MapView = ({
  onLocationSelect,
  initialLocation,
  provider = 'google',
  style = {},
  region,
  onRegionChange,
  showsUserLocation = true,
  followsUserLocation = true,
}) => {
  const [currentLocation, setCurrentLocation] = useState(
    initialLocation || region || defaultCenter,
  )
  const [map, setMap] = useState(null)
  const mapRef = useRef(null)
  const isMoving = useRef(false)
  const lastLocationRef = useRef(currentLocation)

  useEffect(() => {
    if (region && !isMoving.current) {
      const newLocation = { lat: region.lat, lng: region.lng }
      setCurrentLocation(newLocation)
      lastLocationRef.current = newLocation
      if (map) {
        map.panTo(newLocation)
      }
    }
  }, [region, map])

  useEffect(() => {
    if (showsUserLocation && followsUserLocation && !isMoving.current) {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const location = {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            }
            setCurrentLocation(location)
            lastLocationRef.current = location
            if (map) {
              map.panTo(location)
            }
          },
          (error) => {
            console.error('Error getting location:', error)
          },
        )
      }
    }
  }, [map, showsUserLocation, followsUserLocation])

  const handleLoad = (mapInstance) => {
    setMap(mapInstance)
    mapRef.current = mapInstance
  }

  const updateLocation = useCallback(() => {
    if (map) {
      const center = map.getCenter()
      const newLocation = {
        lat: center.lat(),
        lng: center.lng(),
      }

      // Only update if location has changed significantly
      const hasChanged =
        Math.abs(newLocation.lat - lastLocationRef.current.lat) > 0.00001 ||
        Math.abs(newLocation.lng - lastLocationRef.current.lng) > 0.00001

      if (hasChanged) {
        lastLocationRef.current = newLocation
        setCurrentLocation(newLocation)
        if (onLocationSelect) {
          onLocationSelect(newLocation)
        }
        if (onRegionChange) {
          onRegionChange(newLocation)
        }
      }
    }
  }, [map, onLocationSelect, onRegionChange])

  const handleDragStart = () => {
    isMoving.current = true
  }

  const handleDragEnd = () => {
    isMoving.current = false
    updateLocation()
  }

  const handleZoomChanged = () => {
    if (!isMoving.current) {
      updateLocation()
    }
  }

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <LoadScript
        googleMapsApiKey={process.env.REACT_APP_GOOGLE_API_KEY}
        libraries={['places']}
      >
        <GoogleMap
          mapContainerStyle={{ ...containerStyle, ...style }}
          center={currentLocation}
          zoom={15}
          onLoad={handleLoad}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
          onZoomChanged={handleZoomChanged}
          options={{
            fullscreenControl: false,
            streetViewControl: false,
            mapTypeControl: false,
            styles: mapStyles,
            zoomControlOptions: {
              position: window.google?.maps?.ControlPosition?.RIGHT_CENTER,
            },
          }}
        />
      </LoadScript>
      {/* Fixed center marker */}
      <div
        style={{
          position: 'absolute',
          left: '50%',
          top: '50%',
          transform: 'translate(-50%, -100%)',
          width: '24px',
          height: '24px',
          pointerEvents: 'none',
          zIndex: 1,
        }}
      >
        <svg
          width="24"
          height="24"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"
            fill="#299D6B"
            stroke="#FFFFFF"
            strokeWidth="1"
          />
        </svg>
      </div>
    </div>
  )
}

export default MapView
